import React, { useEffect, useState } from "react";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericButton from "@ui-components/Button/GenericButton";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { DetailRow } from "../Shared/DetailRow";
import GpsFinderModal from "./GpsFinderModal";
import { updateExtenderPopAPI, type ExtenderPopPatchBody } from "./requests/request";
import { apiErrorMessage } from "./extenderPopCarrierAccessData";
import { openStreetMapUrl, shapeSiteLocation, type SiteLocation } from "./cpeSite";
import { gpsText, parseGpsText } from "./siteLocationForm";

type LocationField = "locationName" | "address" | "gps";

interface SiteLocationRowsProps {
  extenderPopId: number;
  tenantId: number;
  location: SiteLocation;
  /** Refetch the site after a save, so other views read the new values. */
  onSaved: () => void;
}

/**
 * Where a CPE site is — Location Name, Address and GPS — each edited in
 * place with its pencil, like the Name. Any user of the tenant edits them.
 * The GPS takes "lat, lon" typed, or "Find GPS" looks the address up.
 */
const SiteLocationRows: React.FC<SiteLocationRowsProps> = ({
  extenderPopId,
  tenantId,
  location: initial,
  onSaved,
}) => {
  const api = useAxiosAuth0();
  const [location, setLocation] = useState(initial);
  const [saving, setSaving] = useState<LocationField | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFinding, setIsFinding] = useState(false);

  // A refetch brings the saved values back; take them. By value: the
  // parent shapes a new object on every render.
  const { locationName, address, latitude, longitude } = initial;
  useEffect(
    () => setLocation({ locationName, address, latitude, longitude }),
    [locationName, address, latitude, longitude]
  );

  const patch = async (field: LocationField, body: ExtenderPopPatchBody) => {
    setSaving(field);
    setError(null);
    try {
      const response = await updateExtenderPopAPI(api, extenderPopId, tenantId, body);
      setLocation(shapeSiteLocation(response.data?.data ?? { ...location, ...body }));
      onSaved();
    } finally {
      setSaving(null);
    }
  };

  // Inline saves show their error under the rows; the row keeps the old value.
  const saveInline = (field: LocationField, body: ExtenderPopPatchBody) =>
    patch(field, body).catch((saveError) =>
      setError(apiErrorMessage(saveError, "Could not save the location"))
    );

  const saveText = (field: "locationName" | "address") => (value: string) =>
    saveInline(field, { [field]: value.trim() || null });

  const saveGps = (value: string) => {
    const parsed = parseGpsText(value);
    if ("error" in parsed) {
      setError(parsed.error);
      return;
    }
    saveInline("gps", parsed.body);
  };

  const editable = (field: LocationField, value: string, onSave: (v: string) => void) => (
    <GenericTag
      label={value || "—"}
      variant="neutral"
      size="small"
      minWidth="100px"
      className="text-center"
      editable={true}
      value={value}
      onSave={onSave}
      isSaving={saving === field}
      disabled={saving !== null}
    />
  );

  const mapUrl = openStreetMapUrl(location.latitude, location.longitude);

  return (
    <>
      <DetailRow
        label="Location Name"
        value={editable("locationName", location.locationName ?? "", saveText("locationName"))}
      />
      <DetailRow
        label="Address"
        value={editable("address", location.address ?? "", saveText("address"))}
      />
      <DetailRow
        label="GPS"
        value={
          <div className="flex items-center justify-end gap-2 flex-wrap">
            {editable("gps", gpsText(location), saveGps)}
            {mapUrl && (
              <a
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap text-sm font-medium underline text-semantic-text-brand"
              >
                Map
              </a>
            )}
            <GenericButton
              variant="secondary"
              size="sm"
              label="Find GPS"
              onClick={() => setIsFinding(true)}
              disabled={saving !== null}
            />
          </div>
        }
      />
      {error && <p className="text-sm text-semantic-text-error text-right">{error}</p>}
      {isFinding && (
        <GpsFinderModal
          onClose={() => setIsFinding(false)}
          onSave={async (point) => {
            await patch("gps", point);
            setIsFinding(false);
          }}
          tenantId={tenantId}
          location={location}
        />
      )}
    </>
  );
};

export default SiteLocationRows;
